import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { StatusList } from './StatusList';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ClinicSettingsConversationsChannelStatusFragment as Status,
  useCreateClinicSettingsConversationsChannelStatusMutation as useCreateStatus,
  useUpdateClinicSettingsConversationsChannelStatusMutation,
  ChannelStatusAction,
  ChannelStatusUpdateInput,
  PermissionType,
  useGetClinicSettingsClinicSettingIdQuery,
} from 'shared/types/graphql';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { channelStatusActionLabels } from 'shared/components/ConversationStatusSelect';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box, Flex, Divider } from '@televet/kibble-ui/build/chakra';
import { InfoIcon } from 'shared/components/InfoIcon';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface Props {
  type: string;
  channelStatuses: Status[];
}

const sortStatusesByOrder = (statusArray: Status[]): Status[] => {
  return statusArray.sort((a, b) => ((a.order || 1) > (b.order || 0) ? 1 : -1));
};

const StatusBuilder = ({ type, channelStatuses }: Props): ReactElement => {
  const { currentClinicId, userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);
  const { primaryIntegrationName, isExportToPimsSupported } = useIntegrationsProvider();
  const toast = useToast();
  const { trackEvent } = useTracking();

  // State
  const [clinicSettingId, setClinicSettingId] = useState<string>('');
  const [lastAdded, setLastAdded] = useState<string>('');
  const [activeStatuses, setActiveStatuses] = useState<Status[]>([]);
  const [closedStatuses, setClosedStatuses] = useState<Status[]>([]);
  const [archivedStatuses, setArchivedStatuses] = useState<Status[]>([]);
  const [statusListsLoading, setStatusListsLoading] = useState(new Set<string>());

  const { data: clinicData } = useGetClinicSettingsClinicSettingIdQuery({
    variables: {
      where: {
        id: currentClinicId,
      },
    },
    onCompleted: (): void => {
      setStatusListsLoading(new Set());
    },
  });

  const [createChannelStatus, { loading: creatingStatus }] = useCreateStatus({
    onCompleted: (data): void => {
      const id = data.createOneChannelStatus.id;
      setLastAdded(id);
      setStatusListsLoading(new Set());
    },
    update: (cache, mutationResult): void => {
      cache.modify({
        fields: {
          findManyChannelStatus(existingChannelStatuses = [], { toReference }): Status[] {
            if (mutationResult?.data) {
              return [...existingChannelStatuses, toReference(mutationResult.data?.createOneChannelStatus)];
            }

            return existingChannelStatuses;
          },
        },
      });
    },
  });

  const [updateChannelStatus] = useUpdateClinicSettingsConversationsChannelStatusMutation();

  useEffect(() => {
    const clinicSettingId = clinicData?.findUniqueClinic?.clinicSetting?.id;
    if (clinicSettingId) {
      setClinicSettingId(clinicSettingId);
    }
  }, [clinicData]);

  useEffect(() => {
    const activeStatuses = [];
    const closedStatuses = [];
    const archivedStatuses = [];

    if (channelStatuses) {
      for (const status of channelStatuses) {
        const { channelStatusAction } = status;
        switch (channelStatusAction) {
          case ChannelStatusAction.Active:
            activeStatuses.push(status);
            break;
          case ChannelStatusAction.Inactive:
            closedStatuses.push(status);
            break;
          case ChannelStatusAction.InactivePermanently:
            archivedStatuses.push(status);
            break;
        }
      }
    }

    setActiveStatuses(sortStatusesByOrder(activeStatuses));
    setClosedStatuses(sortStatusesByOrder(closedStatuses));
    setArchivedStatuses(sortStatusesByOrder(archivedStatuses));
  }, [channelStatuses]);

  // Handlers
  const handleAddStatus = (
    channelStatusAction: ChannelStatusAction,
    color: string,
    name: string,
    order: number,
    statusType: string,
  ): void => {
    handleStatusListLoading(statusType, true);

    createChannelStatus({
      variables: {
        channelStatusAction,
        clinicSettingId,
        color,
        name,
        order,
      },
    });

    trackEvent(SettingsConversationEvents.AddStatus, { statusType });
  };

  const handleChannelStatusUpdate = (channelStatusId: string, data: ChannelStatusUpdateInput): void => {
    updateChannelStatus({
      variables: {
        channelStatusId,
        data,
      },
    });
    toast({ title: 'Channel status updated', status: 'success' });
  };

  const handleStatusListLoading = (actionName: string, loading: boolean): void => {
    if (loading) {
      if (!statusListsLoading.has(actionName)) {
        const updatedList = new Set(statusListsLoading).add(actionName);
        setStatusListsLoading(updatedList);
      }
    }
    //this else portion is not used at the moment by child elements/ instead setStatusListsLoading(new Set()); is just used in this component to hide the loading spinner
    else {
      const updatedList = new Set(statusListsLoading);
      if (statusListsLoading.has(actionName)) {
        updatedList.delete(actionName);
        setStatusListsLoading(updatedList);
      }
    }
  };

  const availableActions = useMemo(() => {
    const active = {
      enum: ChannelStatusAction.Active,
      name: `${channelStatusActionLabels[ChannelStatusAction.Active]}`,
      shortName: channelStatusActionLabels[ChannelStatusAction.Active],
      helper: 'Statuses for managing open conversations and their workflow.',
      defaultColor: '#72abff',
      statuses: activeStatuses,
      initialOrder: 1,
    };
    const closed = {
      enum: ChannelStatusAction.Inactive,
      name: `${channelStatusActionLabels[ChannelStatusAction.Inactive]}`,
      shortName: channelStatusActionLabels[ChannelStatusAction.Inactive],
      helper: 'Marks the conversation as closed but can be reopened by the client.',
      defaultColor: '#484848',
      statuses: closedStatuses,
      initialOrder: 101,
    };
    const archived = {
      enum: ChannelStatusAction.InactivePermanently,
      name: `${channelStatusActionLabels[ChannelStatusAction.InactivePermanently]}`,
      shortName: channelStatusActionLabels[ChannelStatusAction.InactivePermanently],
      helper: 'Marks the conversation as closed and prevents the client from reopening it.',
      defaultColor: '#878890',
      statuses: archivedStatuses,
      initialOrder: 201,
    };
    return [active, closed, archived].filter((category) => !!category.statuses.length);
  }, [activeStatuses, closedStatuses, archivedStatuses]);

  return (
    <>
      <Heading size="sm" mt={5}>
        Conversation Statuses
      </Heading>
      {availableActions.map((action, index) => {
        return (
          <Box key={`available-${type}-status-${index}`} mt={8}>
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <Heading size="xs">{action.shortName}</Heading>
                <InfoIcon text={action.helper} />
              </Flex>
              {hasPermission && action.name !== channelStatusActionLabels[ChannelStatusAction.Inactive] && (
                <Button
                  variant="secondary"
                  size="xs"
                  iconName="plus"
                  isLoading={creatingStatus}
                  onClick={(): void => {
                    handleAddStatus(
                      action.enum,
                      action.defaultColor,
                      `New Status`,
                      action.initialOrder + action.statuses.length,
                      action.name,
                    );
                  }}
                >
                  Add {action.shortName} Status
                </Button>
              )}
            </Flex>
            <Flex px={2} gap={2}>
              <Box display="inline-block" h={10} w={8} />
              <Flex align="center" flex="1">
                <Text fontSize="10px">STATUS NAME</Text>
              </Flex>
              <Flex align="center" flex="1">
                <Text fontSize="10px">COLOR</Text>
              </Flex>
              <Flex align="center" flex="1">
                <Text fontSize="10px">ASSIGNED TO</Text>
              </Flex>
              {isExportToPimsSupported && (
                <Flex align="center" flex="1" key={`integration-${index}`}>
                  <Text fontSize="10px">EXPORT TO {primaryIntegrationName?.toUpperCase()}</Text>
                </Flex>
              )}
              <Box display="inline-block" h={10} w={10} />
            </Flex>
            <Divider />
            <StatusList
              statusType={action.name}
              statuses={action.statuses}
              filter={action.enum}
              handleChannelStatusUpdate={handleChannelStatusUpdate}
              lastAdded={lastAdded}
              setLastAdded={setLastAdded}
              statusListLoading={handleStatusListLoading}
            />
          </Box>
        );
      })}
    </>
  );
};

export default StatusBuilder;
